import React, { useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { ADMIN } from "@/constants/testIds";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const location = useLocation();

  if (user && user !== false && user !== null) {
    const from = location.state?.from?.pathname || "/admin";
    return <Navigate to={from} replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error || "Login failed");
  };

  return (
    <div className="min-h-screen bg-[#050505] text-white flex items-center justify-center px-5">
      <div className="w-full max-w-md">
        <div className="mb-10 flex justify-center">
          <Logo size="md" />
        </div>
        <form
          onSubmit={submit}
          data-testid={ADMIN.loginForm}
          className="border border-white/10 bg-white/[0.02] backdrop-blur-md p-8 space-y-6"
        >
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="h-px w-8 bg-cyan-400" />
              <span className="text-cyan-400 uppercase tracking-[0.35em] text-xs font-medium">
                Admin
              </span>
            </div>
            <h1 className="font-heading text-3xl font-black uppercase tracking-tighter text-white">
              Sign in
            </h1>
            <p className="text-sm text-zinc-400 mt-2">
              Access your dashboard to manage content.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="a-email" className="uppercase tracking-widest text-xs text-zinc-400">
              Email
            </Label>
            <Input
              id="a-email"
              type="email"
              data-testid={ADMIN.loginEmail}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
              required
              autoComplete="email"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="a-password" className="uppercase tracking-widest text-xs text-zinc-400">
              Password
            </Label>
            <Input
              id="a-password"
              type="password"
              data-testid={ADMIN.loginPassword}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="rounded-none bg-black/50 border-white/10 focus:border-cyan-400 focus:ring-cyan-400 text-white h-12"
              required
              autoComplete="current-password"
            />
          </div>

          {error && (
            <div
              data-testid={ADMIN.loginError}
              className="text-sm text-red-400 border border-red-500/30 bg-red-500/10 px-4 py-3"
            >
              {error}
            </div>
          )}

          <Button
            type="submit"
            data-testid={ADMIN.loginSubmit}
            disabled={loading}
            className="rounded-none bg-cyan-400 hover:bg-cyan-300 text-black font-bold uppercase tracking-widest text-sm h-12 w-full shadow-[0_0_20px_rgba(0,229,255,0.3)]"
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 w-4 h-4 animate-spin" />
                Signing in...
              </>
            ) : (
              <>
                <LogIn className="mr-2 w-4 h-4" />
                Sign In
              </>
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
